import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import localeData from 'dayjs/plugin/localeData';
import 'dayjs/locale/fr';
import 'dayjs/locale/en';

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);
dayjs.extend(localizedFormat);
dayjs.extend(duration);
dayjs.extend(isBetween);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(localeData);

// Canonical, locale-independent format for schedule form time fields (start_date,
// end_date, block_*). Both time pickers emit this and every producer of an initial
// value formats with it, so the stored form value is always 24-hour "HH:mm"
// regardless of locale. Localization happens only at display time via .format('LT').
export const TIME_FORMAT = 'HH:mm';

// Canonical display format for calendar day pickers and schedule day labels
// (e.g. "Friday, Jul 10, 2026").
export const DATE_FORMAT = 'dddd, MMM D, YYYY';

// Parses a canonical "HH:mm" schedule form time string into a wall-clock dayjs.
//
// Parsed in UTC on purpose: the result is only ever used to read the wall-clock
// hour/minute (via .get('hour') / .format('LT')), which callers then apply onto a
// timezone-anchored date with dayjs.tz(...). Parsing in browser-local time would tie
// the value to today's local date and offset, letting DST normalization shift the
// hour (e.g. 02:30 -> 03:30 on a spring-forward day) and risk offset leakage. UTC has
// no DST and no offset, so the extracted hour/minute always equal what was entered.
export const parseTimeStr = str => dayjs.utc(str, TIME_FORMAT, true);

// Converts a Date / dayjs object to a YYYY-MM-DD string in local (wall-clock) time.
export const toDateString = d => {
  if (!d) return null;
  // Format dayjs in place — .toDate() can shift the calendar day for MUI picker
  // values stored at UTC midnight when the browser is behind UTC.
  if (dayjs.isDayjs(d)) {
    return d.format('YYYY-MM-DD');
  }
  return dayjs(d).format('YYYY-MM-DD');
};

// Combines a calendar day (Date/dayjs) and a schedule form time string into a
// single dayjs anchored in the given timezone. This is the canonical way the
// schedule forms turn their separate day + time fields into a real datetime.
export const combineDayAndTime = (day, timeStr, tz) => {
  const time = parseTimeStr(timeStr);
  return dayjs
    .tz(toDateString(day), tz)
    .hour(time.get('hour'))
    .minute(time.get('minute'))
    .startOf('minute');
};

export default dayjs;
